import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Snackbar from "@mui/material/Snackbar";
import {
  fetchEnterpriseById,
  updateEnterprise,
  createEnterpriseUser,
  deleteEnterpriseUser,
  getUserDetails,
} from "../../Utils/functions";
import "./EnterpriseDetail.css";
import { Container } from "@mui/material";

function EnterpriseDetail() {
  const { enterpriseId } = useParams();
  const [enterprise, setEnterprise] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isMedRPTAdmin, setIsMedRPTAdmin] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const enterpriseData = await fetchEnterpriseById(enterpriseId);
        setEnterprise(enterpriseData.enterpriseData);
        setUsers(enterpriseData.enterpriseUsers || []);
      } catch (err) {
        setError(
          err.message || "Failed to fetch enterprise details. Please try again."
        );
        console.error("Error fetching enterprise:", err);
      } finally {
        setLoading(false);
      }
    };
    const checkAdminStatus = async () => {
      try {
        const userDetails = await getUserDetails();
        setIsMedRPTAdmin(userDetails?.user?.isMedRPTAdmin || false);
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsMedRPTAdmin(false);
      }
    };
    if (enterpriseId) {
      fetchData();
      checkAdminStatus();
    }
  }, [enterpriseId]);

  const handleNameChange = (e) => {
    setEnterprise({ ...enterprise, name: e.target.value });
  };

  const handleCapacityChange = (e) => {
    setEnterprise({ ...enterprise, capacity: e.target.value });
  };

  const handleExpiryDateChange = (e) => {
    setEnterprise({ ...enterprise, expiryDate: e.target.value });
  };

  const handleIsActiveToggle = async (e) => {
    const newIsActive = e.target.checked;
    setEnterprise({ ...enterprise, isActive: newIsActive });
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateEnterprise(enterpriseId, {
        name: enterprise.name,
        capacity: enterprise.capacity,
        isActive: enterprise.isActive,
        expiryDate: enterprise.expiryDate,
      });
      setSnackbar({
        open: true,
        message: "Enterprise updated successfully!",
        severity: "success",
      });
    } catch (err) {
      setError(err.message || "Failed to save changes. Please try again.");
      console.error("Error updating enterprise:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUserClick = () => {
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!newUserEmail.trim()) {
      setError("User email is required.");
      return;
    }
    setError(null);
    try {
      await createEnterpriseUser(enterpriseId, {
        email: newUserEmail,
        isAdmin: newUserIsAdmin,
      });
      setCreateUserDialogOpen(false);
      setNewUserEmail("");
      setNewUserIsAdmin(true);
      setSnackbar({
        open: true,
        message: "User created successfully.",
        severity: "success",
      });
      // Refresh users list
      const enterpriseData = await fetchEnterpriseById(enterpriseId);
      setUsers(enterpriseData.enterpriseUsers || []);
    } catch (error) {
      setError(error.message || "Failed to create user. Please try again.");
      console.error("Error creating user:", error);
    }
  };

  const handleDeleteUserClick = (userId) => {
    setUserToDelete(userId);
    setDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    setError(null);
    try {
      await deleteEnterpriseUser(enterpriseId, userToDelete);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      setSnackbar({
        open: true,
        message: "User deleted successfully!",
        severity: "success",
      });
      // Refresh users list
      const enterpriseData = await fetchEnterpriseById(enterpriseId);
      setUsers(enterpriseData.enterpriseUsers || []);
    } catch (err) {
      setError(err.message || "Failed to delete user. Please try again.");
      console.error("Error deleting user:", err);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <div className="enterprise-detail-container">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (!enterprise) {
    return (
      <div className="enterprise-detail-container">
        <Alert severity="error">{error || "Enterprise not found."}</Alert>
      </div>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box className="enterprise-details-section">
        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom variant="h6" component="h6" noWrap>
            Name
          </Typography>
          <TextField
            fullWidth
            value={enterprise.name || ""}
            onChange={handleNameChange}
            variant="outlined"
            size="small"
            disabled={!isMedRPTAdmin}
            InputLabelProps={{
              sx: {
                "&.Mui-focused": { color: "#000" },
              },
            }}
            sx={{
              mb: 2,
              backgroundColor: !isMedRPTAdmin ? "#f5f5f5" : "inherit",
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: " #49994C",
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, p: 0 }}>
            <Typography gutterBottom variant="h6" component="h6" noWrap>
              Capacity
            </Typography>
            <TextField
              fullWidth
              value={enterprise.capacity || ""}
              onChange={handleCapacityChange}
              variant="outlined"
              size="small"
              type="number"
              disabled={!isMedRPTAdmin}
              InputLabelProps={{
                sx: {
                  "&.Mui-focused": { color: "#000" },
                },
              }}
              sx={{
                mb: 2,
                backgroundColor: !isMedRPTAdmin ? "#f5f5f5" : "inherit",
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: " #49994C",
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, p: 0 }}>
            <Typography gutterBottom variant="h6" component="h6" noWrap>
              Number Of Users
            </Typography>
            <TextField
              fullWidth
              value={users.length || 0}
              variant="outlined"
              size="small"
              disabled
              InputLabelProps={{
                sx: {
                  "&.Mui-focused": { color: "#000" },
                },
              }}
              sx={{
                mb: 2,
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: " #49994C",
                },
              }}
            />
          </Box>
          <Box sx={{ flex: 1, p: 0 }}>
            <Typography gutterBottom variant="h6" component="h6" noWrap>
              Expiry Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              value={
                enterprise.expiryDate
                  ? enterprise.expiryDate.includes("T")
                    ? enterprise.expiryDate.split("T")[0]
                    : enterprise.expiryDate
                  : ""
              }
              onChange={handleExpiryDateChange}
              variant="outlined"
              size="small"
              disabled={!isMedRPTAdmin}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split("T")[0], // makes sure that we don't select previous dates
              }}
              sx={{
                mb: 2,
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: " #49994C",
                },
              }}
            />
          </Box>

          {isMedRPTAdmin && (
            <Box sx={{ flex: 1, p: 0 }}>
              <Typography gutterBottom variant="h6" component="h6" noWrap>
                IsActive
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={enterprise.isActive || false}
                    onChange={handleIsActiveToggle}
                    disabled={saving}
                    color="success"
                  />
                }
                label={enterprise.isActive ? "Active" : "Inactive"}
              />
            </Box>
          )}
        </Box>

        {isMedRPTAdmin && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              "@media (max-width: 767px)": {
                justifyContent: "center",
              },
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={saving}
              sx={{
                mb: 3,
                backgroundColor: " #49994C",
                "&:hover": { backgroundColor: "darkgreen" },
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        )}
      </Box>

      <Box className="enterprise-users-section">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            "@media (max-width: 767px)": {
              flexDirection: "column-reverse",
              gap: 5,
            },
          }}
        >
          <Typography variant="h6">Users</Typography>
          <Button
            variant="contained"
            onClick={handleCreateUserClick}
            sx={{
              mb: 3,
              "@media (max-width: 767px)": {
                mb: 0,
              },
              backgroundColor: " #49994C",
              "&:hover": { backgroundColor: "darkgreen" },
            }}
          >
            Create User
          </Button>
        </Box>
        <Box>
          {users.length === 0 ? (
            <Typography variant="body1" sx={{ p: 0 }}>
              No users found.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="users table">
                <TableHead>
                  <TableRow>
                    <TableCell>User Email</TableCell>
                    <TableCell>Is Admin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.EnterpriseUserRole === "ADMIN" ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteUserClick(user.userId)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      <Dialog
        open={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        aria-labelledby="create-user-dialog-title"
      >
        <DialogTitle id="create-user-dialog-title">Create User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            InputLabelProps={{
              sx: {
                "&.Mui-focused": { color: "#000" },
              },
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: " #49994C",
              },
            }}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={newUserIsAdmin}
                onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                color="success"
              />
            }
            label="Is Admin"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateUserDialogOpen(false)}
            sx={{
              color: " #49994C",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!newUserEmail.trim()}
            sx={{
              backgroundColor: " #49994C",
              "&:hover": { backgroundColor: "darkgreen" },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteUserDialogOpen}
        onClose={() => setDeleteUserDialogOpen(false)}
        aria-labelledby="delete-user-dialog-title"
      >
        <DialogTitle id="delete-user-dialog-title">Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteUserDialogOpen(false)}
            sx={{
              color: " #49994C",
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    // <div className="enterprise-detail-container">

    // </div>
  );
}

export default EnterpriseDetail;
